import { verifyNativeDocumentText } from '../public-format/native-text';
import { PORTABLE_TEXT_FEATURE, validateNativePortableText } from '../public-format/native-text-contract';
import { prepareNativeDocumentResources, releaseNativeDocumentResources } from '../public-format/native-resources';
import { strFromU8, strToU8, unzip, zip, type AsyncZippable } from 'fflate';
import type { Matrix } from '../core/geometry';
import {
  DEFAULT_LIMITS,
  MAX_LAYER_NESTING,
  layerNestingDepth,
  validateDimensions,
  walkLayers,
  type AdjustmentLayer,
  type BlendMode,
  type EditorDocument,
  type ImportNotice,
  type Layer,
  type LayerBase,
  type LayerColor,
  type LayerMask,
  type TextLayer,
  type VectorLayer,
} from '../core/model';
import { canonicalizeRegions, type SelectionState } from '../core/selection';
import { RasterTileStore } from '../core/tiles';
import type { CodecContext, DocumentCodec } from '../codecs/types';
import { throwIfAborted } from '../codecs/types';
import { migrateVectorScene } from '../vector/migration';

/**
 * A project-format limit (nesting depth, counts, sizes) rather than a malformed
 * value. The semantic dispatcher reports it to agents as LIMIT_EXCEEDED.
 */
export class ProjectLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectLimitError';
  }
}

const FORMAT = 'maipo-project';
const IMAGE_FEATURE = 'encoded-images-v1';
const WRITER_VERSION = '0.1.0';
const MAX_ARCHIVE_ENTRIES = 50_000;
const MAX_EXPANDED_BYTES = 2 * 1024 * 1024 * 1024;
const MAX_JSON_BYTES = 32 * 1024 * 1024;
const MAX_LAYER_DEPTH = MAX_LAYER_NESTING;
const MAX_SELECTION_REGIONS = 10_000;
const UNKNOWN_FIELDS_KEY = 'maipo:unknown-fields';

interface ResourceRecord {
  path: string;
  kind: 'raster-tile' | 'mask-tile';
  layerId: string;
  x: number;
  y: number;
  bytes: number;
  crc32: string;
}

interface ProjectManifest {
  format: typeof FORMAT;
  schemaVersion: 1 | 2 | 3;
  requiredFeatures?: string[];
  writerVersion: string;
  createdAt: string;
  savedAt: string;
  documentId: string;
  entryCount: number;
  expandedBytes: number;
  resources: ResourceRecord[];
}

interface SerializedBase {
  id: string;
  name: string;
  visible: boolean;
  opacity: number;
  fillOpacity: number;
  blendMode: BlendMode;
  locked: boolean;
  pixelsLocked: boolean;
  positionLocked: boolean;
  color: LayerColor;
  clipping: boolean;
  transform: Matrix;
  mask?: SerializedMask;
  extensions: Record<string, unknown>;
}

interface SerializedMask {
  id: string;
  enabled: boolean;
  linked: boolean;
  defaultAlpha: 0 | 255;
  width: number;
  height: number;
  tileSize: number;
  resources: string[];
}

interface SerializedRasterLayer extends SerializedBase {
  kind: 'raster';
  raster: { width: number; height: number; tileSize: number; resources: string[] };
}

interface SerializedGroupLayer extends SerializedBase {
  kind: 'group';
  children: SerializedLayer[];
  expanded: boolean;
}

interface SerializedAdjustmentLayer extends SerializedBase {
  kind: 'adjustment';
  adjustment: AdjustmentLayer['adjustment'];
}

interface SerializedVectorLayer extends SerializedBase {
  kind: 'vector';
  vector: VectorLayer['vector'];
}

interface SerializedTextLayer extends SerializedBase {
  kind: 'text';
  text: TextLayer['text'];
}

interface SerializedImageLayer extends SerializedBase {
  kind: 'image';
  image: { asset: string; frame: { width: number; height: number; fit: 'fill' | 'contain' | 'cover' } };
}

type SerializedLayer = SerializedImageLayer | SerializedRasterLayer | SerializedGroupLayer | SerializedAdjustmentLayer | SerializedVectorLayer | SerializedTextLayer;

interface SerializedDocumentV1 {
  schemaVersion: 1;
  id: string;
  name: string;
  width: number;
  height: number;
  resolution: EditorDocument['resolution'];
  color: EditorDocument['color'];
  pixelAspectRatio: number;
  root: SerializedGroupLayer;
  selection: SelectionState;
  activeLayerIds: string[];
  createdAt: string;
  updatedAt: string;
  source?: EditorDocument['source'];
  importReport?: EditorDocument['importReport'];
  guides?: EditorDocument['guides'];
  extensions: Record<string, unknown>;
}

export interface ProjectOpenResult {
  document: EditorDocument;
  notices: ImportNotice[];
}

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < table.length; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    table[index] = value >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): string {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = (crcTable[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  return ((crc ^ 0xffffffff) >>> 0).toString(16).padStart(8, '0');
}

function safePath(path: string): boolean {
  return path.length > 0 && !path.startsWith('/') && !path.includes('\0') && !path.split('/').includes('..');
}

export function preflightMaipoArchive(bytes: Uint8Array): void {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const minimum = Math.max(0, bytes.byteLength - 65_557);
  let eocd = -1;
  for (let offset = bytes.byteLength - 22; offset >= minimum; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      eocd = offset;
      break;
    }
  }
  if (eocd < 0) throw new Error('Maipo archive has no valid ZIP directory.');
  const entryCount = view.getUint16(eocd + 10, true);
  const centralOffset = view.getUint32(eocd + 16, true);
  if (entryCount > MAX_ARCHIVE_ENTRIES) throw new ProjectLimitError('Maipo archive contains too many entries.');
  let offset = centralOffset;
  let expanded = 0;
  const paths = new Set<string>();
  const decoder = new TextDecoder();
  for (let index = 0; index < entryCount; index += 1) {
    if (offset + 46 > bytes.byteLength || view.getUint32(offset, true) !== 0x02014b50) throw new Error('Maipo ZIP directory is malformed.');
    const uncompressed = view.getUint32(offset + 24, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const nameStart = offset + 46;
    const nameEnd = nameStart + nameLength;
    if (nameEnd > bytes.byteLength) throw new Error('Maipo ZIP entry name is truncated.');
    const path = decoder.decode(bytes.subarray(nameStart, nameEnd));
    if (!safePath(path) || paths.has(path)) throw new Error(`Unsafe or duplicate archive path: ${path || '(empty)'}.`);
    paths.add(path);
    expanded += uncompressed;
    if (!Number.isSafeInteger(expanded) || expanded > MAX_EXPANDED_BYTES) throw new ProjectLimitError('Maipo archive exceeds the expanded data limit.');
    offset = nameEnd + extraLength + commentLength;
  }
}

function zipAsync(entries: AsyncZippable): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(entries, { level: 3 }, (error, result) => {
      if (error) reject(error);
      else resolve(result);
    });
  });
}

function unzipAsync(bytes: Uint8Array): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => {
    unzip(bytes, (error, result) => {
      if (error) reject(error);
      else resolve(result);
    });
  });
}

function cleanExtensions(extensions: Record<string, unknown>): Record<string, unknown> {
  try {
    return JSON.parse(JSON.stringify(extensions)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function serializedExtensions(extensions: Record<string, unknown>): { extensions: Record<string, unknown>; unknown: Record<string, unknown> } {
  const clean = cleanExtensions(extensions);
  const unknown = clean[UNKNOWN_FIELDS_KEY];
  delete clean[UNKNOWN_FIELDS_KEY];
  return {
    extensions: clean,
    unknown: unknown && typeof unknown === 'object' && !Array.isArray(unknown) ? cleanExtensions(unknown as Record<string, unknown>) : {},
  };
}

function preserveUnknownFields(value: object, known: readonly string[], extensions: Record<string, unknown> | undefined): Record<string, unknown> {
  const preserved = Object.fromEntries(Object.entries(value).filter(([key]) => !known.includes(key) && key !== 'dirty'));
  const clean = cleanExtensions(extensions ?? {});
  return Object.keys(preserved).length ? { ...clean, [UNKNOWN_FIELDS_KEY]: preserved } : clean;
}

function base(layer: LayerBase, mask: SerializedMask | undefined): SerializedBase {
  const extensionData = serializedExtensions(layer.extensions);
  return {
    ...extensionData.unknown,
    id: layer.id,
    name: layer.name,
    visible: layer.visible,
    opacity: layer.opacity,
    fillOpacity: layer.fillOpacity,
    blendMode: layer.blendMode,
    locked: layer.locked,
    pixelsLocked: layer.pixelsLocked,
    positionLocked: layer.positionLocked,
    color: layer.color,
    clipping: layer.clipping,
    transform: layer.transform,
    ...(mask ? { mask } : {}),
    extensions: extensionData.extensions,
  };
}

function resourcePath(kind: 'raster' | 'masks', layerId: string, x: number, y: number): string {
  return `${kind}/${encodeURIComponent(layerId)}/${x}_${y}.rgba`;
}

function serializeTileStore(
  store: RasterTileStore,
  layerId: string,
  kind: 'raster' | 'masks',
  entries: Record<string, Uint8Array> | undefined,
  resources: ResourceRecord[],
): string[] {
  const paths: string[] = [];
  for (const tile of store.entries()) {
    const path = resourcePath(kind, layerId, tile.x, tile.y);
    const resourceKind = kind === 'raster' ? 'raster-tile' : 'mask-tile';
    if (entries) {
      const bytes = new Uint8Array(tile.pixels.buffer.slice(tile.pixels.byteOffset, tile.pixels.byteOffset + tile.pixels.byteLength));
      entries[path] = bytes;
      resources.push({ path, kind: resourceKind, layerId, x: tile.x, y: tile.y, bytes: bytes.byteLength, crc32: crc32(bytes) });
    } else {
      // Structure-only pass (assertDocumentSaveable): same records, no pixel copy or checksum.
      resources.push({ path, kind: resourceKind, layerId, x: tile.x, y: tile.y, bytes: tile.pixels.byteLength, crc32: '00000000' });
    }
    paths.push(path);
  }
  return paths;
}

function serializeMask(mask: LayerMask, layerId: string, entries: Record<string, Uint8Array> | undefined, resources: ResourceRecord[]): SerializedMask {
  return {
    id: mask.id,
    enabled: mask.enabled,
    linked: mask.linked,
    defaultAlpha: mask.defaultAlpha,
    width: mask.tiles.width,
    height: mask.tiles.height,
    tileSize: mask.tiles.tileSize,
    resources: serializeTileStore(mask.tiles, layerId, 'masks', entries, resources),
  };
}

function serializeLayer(layer: Layer, entries: Record<string, Uint8Array> | undefined, resources: ResourceRecord[]): SerializedLayer {
  const serializedMask = layer.mask ? serializeMask(layer.mask, layer.id, entries, resources) : undefined;
  if (layer.kind === 'raster') {
    return {
      ...base(layer, serializedMask),
      kind: 'raster',
      raster: {
        width: layer.raster.width,
        height: layer.raster.height,
        tileSize: layer.raster.tileSize,
        resources: serializeTileStore(layer.raster, layer.id, 'raster', entries, resources),
      },
    };
  }
  if (layer.kind === 'group') {
    return {
      ...base(layer, serializedMask),
      kind: 'group',
      children: layer.children.map((child) => serializeLayer(child, entries, resources)),
      expanded: layer.expanded,
    };
  }
  if (layer.kind === 'adjustment') return { ...base(layer, serializedMask), kind: 'adjustment', adjustment: structuredClone(layer.adjustment) };
  if (layer.kind === 'vector') return { ...base(layer, serializedMask), kind: 'vector', vector: structuredClone(layer.vector) };
  if (layer.kind === 'image') {
    validateImageReference(layer.image);
    return { ...base(layer, serializedMask), kind: 'image', image: structuredClone(layer.image) };
  }
  return { ...base(layer, serializedMask), kind: 'text', text: structuredClone(layer.text) };
}

interface ArchiveParts {
  entries: Record<string, Uint8Array>;
  resources: ResourceRecord[];
  serialized: SerializedDocumentV1;
  manifest: ProjectManifest;
  stats: { count: number; expanded: number };
}

/**
 * Builds and validates everything a save writes. `copyBytes: false` is the
 * structure-only pass: identical records and identical validation, but the
 * tile bytes are neither copied nor checksummed.
 */
function buildArchive(document: EditorDocument, copyBytes: boolean): ArchiveParts {
  // Checked first and iteratively: the recursive passes below must never see a tree deeper than the format allows.
  const depth = layerNestingDepth(document);
  if (depth > MAX_LAYER_DEPTH) throw new ProjectLimitError(`Maipo layer nesting exceeds the safety limit: depth ${depth} is deeper than ${MAX_LAYER_DEPTH}.`);
  const entries: Record<string, Uint8Array> = {};
  const resources: ResourceRecord[] = [];
  const extensionData = serializedExtensions(document.extensions);
  const serialized: SerializedDocumentV1 = {
    ...extensionData.unknown,
    schemaVersion: 1,
    id: document.id,
    name: document.name,
    width: document.width,
    height: document.height,
    resolution: document.resolution,
    color: document.color,
    pixelAspectRatio: document.pixelAspectRatio,
    root: serializeLayer(document.root, copyBytes ? entries : undefined, resources) as SerializedGroupLayer,
    selection: document.selection,
    activeLayerIds: document.activeLayerIds,
    createdAt: document.createdAt,
    updatedAt: document.updatedAt,
    ...(document.source ? { source: document.source } : {}),
    ...(document.importReport ? { importReport: document.importReport } : {}),
    ...(document.guides ? { guides: document.guides.map((guide) => ({ ...guide })) } : {}),
    extensions: extensionData.extensions,
  };
  entries['document.json'] = strToU8(JSON.stringify(serialized, null, 2));
  // Refuse to write what this reader could never open again. The limit is
  // enforced on read, and zip hides the problem completely: a 4MP colour trace
  // produces 33MB of document.json that deflates to a 3.7MB file, so it saves
  // in a moment, looks small on disk, and throws on open with nothing to
  // recover. Failing here costs the user a save; failing on read costs the work.
  if (entries['document.json'].byteLength > MAX_JSON_BYTES) {
    throw new ProjectLimitError('This document is too large to save: its geometry exceeds the project format limit. Reduce the detail or colour count of traced artwork and try again.');
  }
  const expandedBytes = copyBytes
    ? Object.values(entries).reduce((sum, entry) => sum + entry.byteLength, 0)
    : entries['document.json'].byteLength + resources.reduce((sum, item) => sum + item.bytes, 0);
  const layers = walkLayers(document.root);
  for (const layer of layers) if (layer.kind === 'text') validateNativePortableText(layer.text);
  const hasImages = layers.some(layer => layer.kind === 'image');
  const hasPortableText = layers.some(layer => layer.kind === 'text' && layer.text.schemaVersion === 2);
  const requiredFeatures = [...(hasImages ? [IMAGE_FEATURE] : []), ...(hasPortableText ? [PORTABLE_TEXT_FEATURE] : [])];
  const manifest: ProjectManifest = {
    format: FORMAT,
    schemaVersion: hasPortableText ? 3 : hasImages ? 2 : 1,
    ...(requiredFeatures.length ? { requiredFeatures } : {}),
    writerVersion: WRITER_VERSION,
    createdAt: document.createdAt,
    savedAt: new Date().toISOString(),
    documentId: document.id,
    entryCount: resources.length + 2,
    expandedBytes,
    resources,
  };
  const stats = { count: resources.length + 2, expanded: expandedBytes };
  if (copyBytes) {
    entries['manifest.json'] = strToU8(JSON.stringify(manifest, null, 2));
    stats.count = Object.keys(entries).length;
  }
  // A successful save must satisfy the same structural and resource-index
  // checks as an open. JSON encoding alone can turn invalid numbers into null.
  validateManifest(manifest, stats);
  validateSerializedDocument(serialized, manifest);
  return { entries, resources, serialized, manifest, stats };
}

/**
 * Throws what `serializeProject` would throw, without producing the archive:
 * the same structural and limit validation (nesting depth, layer and ID
 * counts, unique ids, numeric ranges, vector and text limits, tile and
 * aggregate memory limits, document.json size), minus tile bytes, compression
 * and asynchronous portable-text font admission. The dispatcher runs it before
 * committing an agent transaction so a committed document is always saveable.
 * Violations of the limit kind are `ProjectLimitError`.
 */
export function assertDocumentSaveable(document: EditorDocument): void {
  buildArchive(document, false);
}

export async function serializeProject(document: EditorDocument, signal?: AbortSignal): Promise<Uint8Array> {
  throwIfAborted(signal);
  const { entries } = buildArchive(document, true);
  await verifyNativeDocumentText(document, signal);
  throwIfAborted(signal);
  return zipAsync(entries);
}

function archiveStats(entries: Record<string, Uint8Array>): { count: number; expanded: number } {
  return {
    count: Object.keys(entries).length,
    expanded: Object.entries(entries).filter(([path]) => path !== 'manifest.json').reduce((sum, [, bytes]) => sum + bytes.byteLength, 0),
  };
}

function parseJson<T>(entry: Uint8Array | undefined, name: string): T {
  if (!entry) throw new Error(`Maipo project is missing ${name}.`);
  if (entry.byteLength > MAX_JSON_BYTES) throw new Error(`${name} exceeds the JSON safety limit.`);
  try {
    return JSON.parse(strFromU8(entry)) as T;
  } catch {
    throw new Error(`${name} contains invalid JSON.`);
  }
}

const blendModes = new Set<BlendMode>(['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion']);
const layerColors = new Set<LayerColor>(['none', 'red', 'orange', 'yellow', 'green', 'blue', 'violet', 'gray']);
const layerKinds = new Set<Layer['kind']>(['raster', 'group', 'adjustment', 'vector', 'text', 'image']);
const baseKeys = ['id', 'name', 'visible', 'opacity', 'fillOpacity', 'blendMode', 'locked', 'pixelsLocked', 'positionLocked', 'color', 'clipping', 'transform', 'mask', 'extensions', 'kind'] as const;

function record(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${label} must be an object.`);
  return value as Record<string, unknown>;
}

function finite(value: unknown, label: string, minimum = -DEFAULT_LIMITS.maxDimension * 4, maximum = DEFAULT_LIMITS.maxDimension * 4): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum) throw new Error(`${label} is outside its supported numeric range.`);
  return value;
}

function nonemptyString(value: unknown, label: string, maximum = 4_096): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > maximum || value.includes('\0')) throw new Error(`${label} is malformed.`);
  return value;
}

function boolean(value: unknown, label: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`${label} must be true or false.`);
  return value;
}

function validateStoreMetadata(
  value: unknown,
  layerId: string,
  kind: 'raster' | 'masks',
  resources: ReadonlyMap<string, ResourceRecord>,
  referenced: Set<string>,
): { width: number; height: number; tileSize: number; resources: string[] } {
  const metadata = record(value, `${kind} tile metadata`);
  const width = finite(metadata.width, `${kind} width`, 1, DEFAULT_LIMITS.maxDimension);
  const height = finite(metadata.height, `${kind} height`, 1, DEFAULT_LIMITS.maxDimension);
  if (!Number.isInteger(width) || !Number.isInteger(height)) throw new Error(`${kind} dimensions must be integers.`);
  validateDimensions(width, height);
  const tileSize = finite(metadata.tileSize, `${kind} tile size`, 32, 1024);
  if (!Number.isInteger(tileSize)) throw new Error(`${kind} tile size must be an integer.`);
  if (!Array.isArray(metadata.resources)) throw new Error(`${kind} resources must be an array.`);
  const paths = metadata.resources.map((path, index) => nonemptyString(path, `${kind} resource ${index}`));
  if (new Set(paths).size !== paths.length) throw new Error(`${kind} resources contain duplicate paths.`);
  for (const path of paths) {
    if (referenced.has(path)) throw new Error(`Tile resource is referenced more than once: ${path}.`);
    referenced.add(path);
    const item = resources.get(path);
    if (!item || item.layerId !== layerId || item.kind !== (kind === 'raster' ? 'raster-tile' : 'mask-tile')) throw new Error(`Tile resource metadata does not match ${path}.`);
    if (path !== resourcePath(kind, layerId, item.x, item.y)) throw new Error(`Tile resource path is not canonical: ${path}.`);
    const columns = Math.ceil(width / tileSize);
    const rows = Math.ceil(height / tileSize);
    if (!Number.isInteger(item.x) || !Number.isInteger(item.y) || item.x < 0 || item.y < 0 || item.x >= columns || item.y >= rows) throw new Error(`Tile coordinates are invalid for ${path}.`);
    const tileWidth = Math.min(tileSize, width - item.x * tileSize);
    const tileHeight = Math.min(tileSize, height - item.y * tileSize);
    if (item.bytes !== tileWidth * tileHeight * 4) throw new Error(`Tile byte count is invalid for ${path}.`);
  }
  return { width, height, tileSize, resources: paths };
}

function validateImageReference(value: unknown): void {
  const image = record(value, 'Image reference');
  nonemptyString(image.asset, 'Image asset ID', 512);
  const frame = record(image.frame, 'Image frame');
  if (!Number.isInteger(frame.width) || !Number.isInteger(frame.height)) throw new Error('Image frame dimensions must be integers.');
  finite(frame.width, 'Image frame width', 1, DEFAULT_LIMITS.maxDimension);
  finite(frame.height, 'Image frame height', 1, DEFAULT_LIMITS.maxDimension);
  if (typeof frame.fit !== 'string' || !['fill', 'contain', 'cover'].includes(frame.fit)) throw new Error('Image fit is unknown.');
  if (Object.keys(image).some(key => !['asset', 'frame'].includes(key)) || Object.keys(frame).some(key => !['width', 'height', 'fit'].includes(key))) throw new Error('Unknown image reference fields.');
}

function validateLayerRecord(
  value: unknown,
  depth: number,
  ids: Set<string>,
  layerIds: Set<string>,
  resources: ReadonlyMap<string, ResourceRecord>,
  referenced: Set<string>,
): void {
  if (depth > MAX_LAYER_DEPTH) throw new ProjectLimitError(`Maipo layer nesting exceeds the safety limit: depth ${depth} is deeper than ${MAX_LAYER_DEPTH}.`);
  const layer = record(value, 'Layer metadata');
  const id = nonemptyString(layer.id, 'Layer ID', 512);
  if (ids.has(id)) throw new Error(`Duplicate layer or mask ID: ${id}.`);
  ids.add(id);
  layerIds.add(id);
  nonemptyString(layer.name, 'Layer name');
  const kind = nonemptyString(layer.kind, 'Layer kind') as Layer['kind'];
  if (!layerKinds.has(kind)) throw new Error(`Unknown layer kind: ${kind}.`);
  boolean(layer.visible, 'Layer visibility');
  finite(layer.opacity, 'Layer opacity', 0, 1);
  finite(layer.fillOpacity, 'Layer fill opacity', 0, 1);
  if (!blendModes.has(layer.blendMode as BlendMode)) throw new Error('Layer blend mode is unknown.');
  for (const field of ['locked', 'pixelsLocked', 'positionLocked', 'clipping'] as const) boolean(layer[field], `Layer ${field}`);
  if (!layerColors.has(layer.color as LayerColor)) throw new Error('Layer color label is unknown.');
  if (!Array.isArray(layer.transform) || layer.transform.length !== 6) throw new Error('Layer transform must contain six numbers.');
  layer.transform.forEach((component, index) => finite(component, `Layer transform component ${index}`, -1_000_000, 1_000_000));
  if (layer.extensions !== undefined) record(layer.extensions, 'Layer extensions');
  let maskDimensions: { width: number; height: number } | undefined;
  if (layer.mask !== undefined) {
    const mask = record(layer.mask, 'Layer mask');
    const maskId = nonemptyString(mask.id, 'Mask ID', 512);
    if (ids.has(maskId)) throw new Error(`Duplicate layer or mask ID: ${maskId}.`);
    ids.add(maskId);
    boolean(mask.enabled, 'Mask enabled');
    boolean(mask.linked, 'Mask linked');
    if (mask.defaultAlpha !== 0 && mask.defaultAlpha !== 255) throw new Error('Mask default alpha must be 0 or 255.');
    maskDimensions = validateStoreMetadata(mask, id, 'masks', resources, referenced);
  }
  if (kind === 'raster') {
    const raster = validateStoreMetadata(layer.raster, id, 'raster', resources, referenced);
    if (maskDimensions && (maskDimensions.width !== raster.width || maskDimensions.height !== raster.height)) throw new Error('Raster mask dimensions do not match its layer store.');
  } else if (kind === 'group') {
    if (!Array.isArray(layer.children)) throw new Error('Group children must be an array.');
    boolean(layer.expanded, 'Group expansion');
    for (const child of layer.children) validateLayerRecord(child, depth + 1, ids, layerIds, resources, referenced);
  } else if (kind === 'adjustment') {
    const adjustment = record(layer.adjustment, 'Adjustment settings');
    finite(adjustment.brightness, 'Brightness', -1, 1);
    finite(adjustment.contrast, 'Contrast', -0.99, 0.99);
    finite(adjustment.saturation, 'Saturation', -1, 1);
    boolean(adjustment.invert, 'Adjustment inversion');
    if (adjustment.levels !== undefined) {
      const levels = record(adjustment.levels, 'Adjustment levels');
      finite(levels.inputBlack, 'Levels input black', 0, 1);
      finite(levels.inputWhite, 'Levels input white', 0, 1);
      finite(levels.gamma, 'Levels gamma', 0.1, 9.99);
      finite(levels.outputBlack, 'Levels output black', 0, 1);
      finite(levels.outputWhite, 'Levels output white', 0, 1);
      if ((levels.inputBlack as number) >= (levels.inputWhite as number)) throw new Error('Levels input range is empty.');
      if ((levels.outputBlack as number) > (levels.outputWhite as number)) throw new Error('Levels output range is reversed.');
    }
    if (adjustment.curves !== undefined) {
      const curves = record(adjustment.curves, 'Adjustment curves');
      for (const channel of ['rgb', 'red', 'green', 'blue'] as const) {
        if (!Array.isArray(curves[channel]) || curves[channel].length > 32) throw new Error(`Adjustment ${channel} curve is malformed.`);
        for (const entry of curves[channel]) {
          const point = record(entry, `Adjustment ${channel} curve point`);
          finite(point.x, 'Curve input', 0, 1);
          finite(point.y, 'Curve output', 0, 1);
        }
      }
    }
    if (adjustment.gradientMap !== undefined) {
      const gradient = record(adjustment.gradientMap, 'Gradient map');
      boolean(gradient.enabled, 'Gradient map enabled');
      boolean(gradient.reverse, 'Gradient map reverse');
      if (!Array.isArray(gradient.stops) || gradient.stops.length < 2 || gradient.stops.length > 32) throw new Error('Gradient map stops are malformed.');
      for (const entry of gradient.stops) {
        const stop = record(entry, 'Gradient map stop');
        finite(stop.position, 'Gradient map stop position', 0, 1);
        if (typeof stop.color !== 'string' || !/^#[\da-f]{6}$/i.test(stop.color)) throw new Error('Gradient map stop color is malformed.');
      }
    }
  } else if (kind === 'image') {
    validateImageReference(layer.image);
  } else if (kind === 'vector') {
    const scene = migrateVectorScene(layer.vector);
    if (scene.objects.length > 100_000) throw new ProjectLimitError('Vector scene exceeds the object safety limit.');
    const objectIds = new Set<string>();
    for (const object of scene.objects) {
      nonemptyString(object.id, 'Vector object ID', 512);
      nonemptyString(object.name, 'Vector object name');
      if (objectIds.has(object.id)) throw new Error(`Duplicate vector object ID: ${object.id}.`);
      objectIds.add(object.id);
      if (!['rectangle', 'ellipse', 'path'].includes(object.type)) throw new Error('Unknown vector object type.');
      boolean(object.visible, 'Vector object visibility');
      finite(object.opacity, 'Vector object opacity', 0, 1);
      if (!Array.isArray(object.transform) || object.transform.length !== 6) throw new Error('Vector transform is malformed.');
      object.transform.forEach((component) => finite(component, 'Vector transform component', -1_000_000, 1_000_000));
      record(object.extensions, 'Vector object extensions');
      if (object.fill) {
        if (object.fill.type !== 'solid' || typeof object.fill.color !== 'string') throw new Error('Vector fill paint is malformed.');
        finite(object.fill.alpha, 'Vector fill alpha', 0, 1);
      }
      if (object.stroke) {
        if (object.stroke.paint.type !== 'solid' || typeof object.stroke.paint.color !== 'string') throw new Error('Vector stroke paint is malformed.');
        finite(object.stroke.paint.alpha, 'Vector stroke alpha', 0, 1);
        finite(object.stroke.width, 'Vector stroke width', 0, 100_000);
        if (!['butt', 'round', 'square'].includes(object.stroke.lineCap) || !['miter', 'round', 'bevel'].includes(object.stroke.lineJoin)) throw new Error('Vector stroke cap or join is unknown.');
        if (!Array.isArray(object.stroke.dash) || object.stroke.dash.length > 1_024) throw new Error('Vector stroke dash pattern is malformed.');
        object.stroke.dash.forEach((value) => finite(value, 'Vector stroke dash', 0, 100_000));
      }
      if (object.type === 'path') {
        if (object.geometry.fillRule !== 'nonzero' && object.geometry.fillRule !== 'evenodd') throw new Error('Path fill rule is unknown.');
        if (object.geometry.nodes.length > 100_000 || object.geometry.subpaths.length > 10_000) throw new ProjectLimitError('Path exceeds the topology safety limit.');
        const nodeIds = new Set<string>();
        const subpathIds = new Set<string>();
        for (const subpath of object.geometry.subpaths) {
          nonemptyString(subpath.id, 'Subpath ID', 512);
          if (subpathIds.has(subpath.id)) throw new Error(`Duplicate subpath ID: ${subpath.id}.`);
          subpathIds.add(subpath.id);
          if (!Array.isArray(subpath.nodeIds) || typeof subpath.closed !== 'boolean') throw new Error('Subpath metadata is malformed.');
        }
        for (const node of object.geometry.nodes) {
          nonemptyString(node.id, 'Path node ID', 512);
          if (nodeIds.has(node.id)) throw new Error(`Duplicate path node ID: ${node.id}.`);
          nodeIds.add(node.id);
          if (!subpathIds.has(node.subpathId) || !['corner', 'smooth', 'symmetric'].includes(node.continuity)) throw new Error('Path node topology is malformed.');
          finite(node.anchor.x, 'Path anchor X'); finite(node.anchor.y, 'Path anchor Y');
          if (node.incoming) { finite(node.incoming.x, 'Path handle X'); finite(node.incoming.y, 'Path handle Y'); }
          if (node.outgoing) { finite(node.outgoing.x, 'Path handle X'); finite(node.outgoing.y, 'Path handle Y'); }
        }
        for (const subpath of object.geometry.subpaths) {
          if (new Set(subpath.nodeIds).size !== subpath.nodeIds.length || subpath.nodeIds.some((id) => !nodeIds.has(id))) throw new Error('Subpath contains duplicate or missing node references.');
        }
      } else {
        finite(object.geometry.x, 'Vector geometry X');
        finite(object.geometry.y, 'Vector geometry Y');
        finite(object.geometry.width, 'Vector geometry width', 0, DEFAULT_LIMITS.maxDimension);
        finite(object.geometry.height, 'Vector geometry height', 0, DEFAULT_LIMITS.maxDimension);
        if (object.type === 'rectangle') for (const radius of Object.values(object.geometry.radius)) finite(radius, 'Rectangle radius', 0, DEFAULT_LIMITS.maxDimension);
      }
    }
  } else {
    const text = record(layer.text, 'Text payload');
    if (text.schemaVersion !== 1 && text.schemaVersion !== 2) throw new Error('Unsupported text payload version.');
    if (typeof text.value !== 'string' || text.value.length > 10_000_000) throw new ProjectLimitError('Text value exceeds the safety limit.');
    const box = record(text.box, 'Text box');
    finite(box.width, 'Text box width', 1, DEFAULT_LIMITS.maxDimension);
    finite(box.height, 'Text box height', 1, DEFAULT_LIMITS.maxDimension);
    boolean(box.autoHeight, 'Text auto-height');
    const style = record(text.style, 'Text style');
    nonemptyString(style.family, 'Text font family');
    finite(style.weight, 'Text weight', 1, 1_000);
    finite(style.size, 'Text size', 1, 10_000);
    finite(style.lineHeight, 'Text line height', 0.1, 100);
    finite(style.tracking, 'Text tracking', -10_000, 10_000);
    if (style.align !== 'left' && style.align !== 'center' && style.align !== 'right') throw new Error('Text alignment is unknown.');
    if (typeof style.color !== 'string' || typeof text.missingFont !== 'boolean') throw new Error('Text color or font state is malformed.');
    record(text.extensions, 'Text extensions');
    validateNativePortableText(text as unknown as TextLayer['text']);
  }
}

function validateManifest(value: unknown, archive: { count: number; expanded: number }): ProjectManifest {
  const manifest = record(value, 'manifest.json') as unknown as ProjectManifest;
  if ((manifest.format !== FORMAT && manifest.format !== 'vireo-project') || ![1, 2, 3].includes(manifest.schemaVersion)) throw new Error('This is not a supported Maipo project.');
  if (manifest.schemaVersion === 1) {
    if (manifest.requiredFeatures !== undefined) throw new Error('Schema 1 cannot declare required features.');
  } else {
    const allowed = manifest.schemaVersion === 2 ? [[IMAGE_FEATURE]] : [[PORTABLE_TEXT_FEATURE], [IMAGE_FEATURE, PORTABLE_TEXT_FEATURE]];
    if (manifest.format !== FORMAT || !allowed.some(features => JSON.stringify(features) === JSON.stringify(manifest.requiredFeatures))) throw new Error(`Unsupported required features in Maipo schema ${manifest.schemaVersion}.`);
  }
  nonemptyString(manifest.writerVersion, 'Writer version', 128);
  nonemptyString(manifest.documentId, 'Manifest document ID', 512);
  if (!Number.isFinite(Date.parse(manifest.createdAt)) || !Number.isFinite(Date.parse(manifest.savedAt))) throw new Error('Manifest timestamps are invalid.');
  if (!Array.isArray(manifest.resources) || manifest.resources.length > MAX_ARCHIVE_ENTRIES - 2) throw new Error('Maipo resource index is malformed.');
  if (!Number.isInteger(manifest.entryCount) || manifest.entryCount !== archive.count) throw new Error('Maipo manifest entry count does not match the archive.');
  const expanded = archive.expanded;
  if (manifest.expandedBytes !== expanded || expanded > MAX_EXPANDED_BYTES) throw new ProjectLimitError('Maipo manifest expanded byte count is invalid.');
  const paths = new Set<string>();
  let decodedBytes = 0;
  for (const itemValue of manifest.resources) {
    const item = record(itemValue, 'Resource record') as unknown as ResourceRecord;
    nonemptyString(item.path, 'Resource path');
    nonemptyString(item.layerId, 'Resource layer ID', 512);
    if (!safePath(item.path) || paths.has(item.path)) throw new Error(`Unsafe or duplicate resource path: ${item.path}.`);
    paths.add(item.path);
    if (item.kind !== 'raster-tile' && item.kind !== 'mask-tile') throw new Error('Unknown resource kind.');
    if (!Number.isInteger(item.bytes) || item.bytes < 0) throw new Error('Resource byte count is invalid.');
    if (!/^[\da-f]{8}$/i.test(item.crc32)) throw new Error('Resource checksum is malformed.');
    decodedBytes += item.bytes;
    if (!Number.isSafeInteger(decodedBytes) || decodedBytes > DEFAULT_LIMITS.maxDecodedBytes) throw new ProjectLimitError('Maipo project exceeds the aggregate decoded memory limit.');
  }
  return manifest;
}

function validateSerializedDocument(serialized: SerializedDocumentV1, manifest: ProjectManifest): void {
  validateDimensions(serialized.width, serialized.height);
  nonemptyString(serialized.id, 'Document ID', 512);
  nonemptyString(serialized.name, 'Document name');
  if (serialized.id !== manifest.documentId) throw new Error('Manifest and document IDs do not match.');
  for (const [label, value] of [['Created time', serialized.createdAt], ['Updated time', serialized.updatedAt]] as const) {
    nonemptyString(value, label, 128);
    if (!Number.isFinite(Date.parse(value))) throw new Error(`${label} is invalid.`);
  }
  finite(serialized.resolution?.x, 'Horizontal resolution', 1, 1_000_000);
  finite(serialized.resolution?.y, 'Vertical resolution', 1, 1_000_000);
  if (serialized.resolution?.unit !== 'ppi' || serialized.color?.space !== 'srgb' || serialized.color?.depth !== 8) throw new Error('Document color or resolution metadata is unsupported.');
  finite(serialized.pixelAspectRatio, 'Pixel aspect ratio', 0.01, 100);
  record(serialized.extensions, 'Document extensions');
  if (serialized.source !== undefined) {
    const source = record(serialized.source, 'Document source');
    if (!['new', 'image', 'psd', 'maipo', 'recovery'].includes(source.kind as string)) throw new Error('Document source kind is unknown.');
    if (source.name !== undefined && typeof source.name !== 'string') throw new Error('Document source name is malformed.');
  }
  if (!Array.isArray(serialized.selection?.regions) || serialized.selection.regions.length > MAX_SELECTION_REGIONS || typeof serialized.selection.inverted !== 'boolean') throw new ProjectLimitError(`Selection metadata is malformed or has more than ${MAX_SELECTION_REGIONS} regions.`);
  for (const value of serialized.selection.regions) {
    const rect = record(value, 'Selection rectangle');
    finite(rect.x, 'Selection X'); finite(rect.y, 'Selection Y'); finite(rect.width, 'Selection width', -DEFAULT_LIMITS.maxDimension, DEFAULT_LIMITS.maxDimension); finite(rect.height, 'Selection height', -DEFAULT_LIMITS.maxDimension, DEFAULT_LIMITS.maxDimension);
    if ((rect.width as number) <= 0 || (rect.height as number) <= 0) throw new Error('Selection rectangles must have positive dimensions.');
  }
  if (!Array.isArray(serialized.activeLayerIds)) throw new Error('Active layer IDs must be an array.');
  const resourceIndex = new Map(manifest.resources.map((item) => [item.path, item]));
  const referenced = new Set<string>();
  const ids = new Set<string>();
  const layerIds = new Set<string>();
  validateLayerRecord(serialized.root, 0, ids, layerIds, resourceIndex, referenced);
  if (serialized.root.kind !== 'group') throw new Error('Maipo root layer is not a group.');
  const containsImage = (layer: SerializedLayer): boolean => layer.kind === 'image'
    || (layer.kind === 'group' && layer.children.some(containsImage));
  const containsPortable = (layer: SerializedLayer): boolean => (layer.kind === 'text' && layer.text.schemaVersion === 2)
    || (layer.kind === 'group' && layer.children.some(containsPortable));
  const expected = [...(containsImage(serialized.root) ? [IMAGE_FEATURE] : []), ...(containsPortable(serialized.root) ? [PORTABLE_TEXT_FEATURE] : [])];
  const expectedVersion = containsPortable(serialized.root) ? 3 : containsImage(serialized.root) ? 2 : 1;
  if (manifest.schemaVersion !== expectedVersion || JSON.stringify(manifest.requiredFeatures ?? []) !== JSON.stringify(expected)) throw new Error('Native layer semantics do not match their exact required features.');

  const activeIds = serialized.activeLayerIds.map((id, index) => nonemptyString(id, `Active layer ID ${index}`, 512));
  if (new Set(activeIds).size !== activeIds.length || activeIds.some((id) => !layerIds.has(id))) throw new Error('Active layer IDs are duplicated or reference a missing layer.');
  if (serialized.guides !== undefined) {
    if (!Array.isArray(serialized.guides) || serialized.guides.length > 10_000) throw new ProjectLimitError('Guide metadata exceeds the safety limit.');
    for (const value of serialized.guides) {
      const guide = record(value, 'Guide metadata');
      const guideId = nonemptyString(guide.id, 'Guide ID', 512);
      if (ids.has(guideId)) throw new Error(`Duplicate document ID: ${guideId}.`);
      ids.add(guideId);
      if (guide.axis !== 'horizontal' && guide.axis !== 'vertical') throw new Error('Guide axis is unknown.');
      finite(guide.position, 'Guide position', 0, guide.axis === 'vertical' ? serialized.width : serialized.height);
      if (guide.color !== undefined && typeof guide.color !== 'string') throw new Error('Guide color is malformed.');
    }
  }
  if (layerIds.size > DEFAULT_LIMITS.maxLayers) throw new ProjectLimitError('Maipo project exceeds the layer limit.');
  if (ids.size > DEFAULT_LIMITS.maxLayers * 2 + 10_001) throw new ProjectLimitError('Maipo project exceeds the ID safety limit.');
  if (referenced.size !== resourceIndex.size) throw new Error('Maipo manifest contains unreferenced tile resources.');
}

function loadStore(
  metadata: { width: number; height: number; tileSize: number; resources: string[] },
  entries: Record<string, Uint8Array>,
  index: ReadonlyMap<string, ResourceRecord>,
  notices: ImportNotice[],
  layerName: string,
): RasterTileStore {
  validateDimensions(metadata.width, metadata.height);
  if (metadata.tileSize < 32 || metadata.tileSize > 1024) throw new Error('Unsupported tile size.');
  const store = new RasterTileStore(metadata.width, metadata.height, metadata.tileSize);
  for (const path of metadata.resources) {
    const record = index.get(path);
    const bytes = entries[path];
    if (!record || !bytes || bytes.byteLength !== record.bytes || crc32(bytes) !== record.crc32) {
      notices.push({ severity: 'error', feature: 'tile-resource', layerName, message: `Tile ${path} is missing or corrupt; it was recovered as transparent.`, handling: 'omitted' });
      continue;
    }
    const expected = store.tileDimensions(record.x, record.y);
    if (bytes.byteLength !== expected.width * expected.height * 4) {
      notices.push({ severity: 'error', feature: 'tile-resource', layerName, message: `Tile ${path} has invalid dimensions; it was recovered as transparent.`, handling: 'omitted' });
      continue;
    }
    store.setTile(record.x, record.y, new Uint8ClampedArray(bytes));
  }
  return store;
}

function deserializeMask(
  mask: SerializedMask,
  entries: Record<string, Uint8Array>,
  index: ReadonlyMap<string, ResourceRecord>,
  notices: ImportNotice[],
  layerName: string,
): LayerMask {
  if (!mask.linked) notices.push({ severity: 'warning', feature: 'independent-mask-transform', layerName, message: 'Independent mask transforms are not supported yet; this mask remains linked to its layer.', handling: 'approximated' });
  return {
    id: mask.id,
    enabled: mask.enabled,
    linked: true,
    defaultAlpha: mask.defaultAlpha,
    tiles: loadStore(mask, entries, index, notices, layerName),
  };
}

function deserializeLayer(
  serialized: SerializedLayer,
  entries: Record<string, Uint8Array>,
  index: ReadonlyMap<string, ResourceRecord>,
  notices: ImportNotice[],
): Layer {
  const extensionKeys = [...baseKeys, serialized.kind === 'raster' ? 'raster' : serialized.kind === 'group' ? 'children' : serialized.kind === 'adjustment' ? 'adjustment' : serialized.kind === 'vector' ? 'vector' : serialized.kind === 'image' ? 'image' : 'text', ...(serialized.kind === 'group' ? ['expanded'] : [])];
  const shared = {
    id: serialized.id,
    name: serialized.name,
    visible: serialized.visible,
    opacity: serialized.opacity,
    fillOpacity: serialized.fillOpacity,
    blendMode: serialized.blendMode,
    locked: serialized.locked,
    pixelsLocked: serialized.pixelsLocked,
    positionLocked: serialized.positionLocked,
    color: serialized.color,
    clipping: serialized.clipping,
    transform: serialized.transform,
    ...(serialized.mask ? { mask: deserializeMask(serialized.mask, entries, index, notices, serialized.name) } : {}),
    extensions: preserveUnknownFields(serialized, extensionKeys, serialized.extensions),
  };
  if (serialized.kind === 'raster') {
    return { ...shared, kind: 'raster', raster: loadStore(serialized.raster, entries, index, notices, serialized.name) };
  }
  if (serialized.kind === 'group') {
    return { ...shared, kind: 'group', children: serialized.children.map((child) => deserializeLayer(child, entries, index, notices)), expanded: serialized.expanded };
  }
  if (serialized.kind === 'adjustment') return { ...shared, kind: 'adjustment', adjustment: structuredClone(serialized.adjustment) };
  if (serialized.kind === 'vector') return { ...shared, kind: 'vector', vector: migrateVectorScene(serialized.vector) };
  if (serialized.kind === 'image') return { ...shared, kind: 'image', image: structuredClone(serialized.image) };
  return { ...shared, kind: 'text', text: structuredClone(serialized.text) };
}

export function migrateProject(value: unknown): SerializedDocumentV1 {
  if (!value || typeof value !== 'object') throw new Error('document.json must contain an object.');
  const candidate = value as { schemaVersion?: unknown };
  if (candidate.schemaVersion !== 1) throw new Error(`Unsupported Maipo schema version: ${String(candidate.schemaVersion)}.`);
  return value as SerializedDocumentV1;
}

export async function deserializeProject(bytes: Uint8Array, signal?: AbortSignal): Promise<ProjectOpenResult> {
  preflightMaipoArchive(bytes);
  throwIfAborted(signal);
  const entries = await unzipAsync(bytes);
  throwIfAborted(signal);
  const manifest = validateManifest(parseJson<unknown>(entries['manifest.json'], 'manifest.json'), archiveStats(entries));
  const serialized = migrateProject(parseJson<unknown>(entries['document.json'], 'document.json'));
  validateSerializedDocument(serialized, manifest);
  const notices: ImportNotice[] = [];
  const resourceIndex = new Map(manifest.resources.map((resource) => [resource.path, resource]));
  const root = deserializeLayer(serialized.root, entries, resourceIndex, notices);
  if (root.kind !== 'group') throw new Error('Maipo root layer is not a group.');
  if (walkLayers(root).length > DEFAULT_LIMITS.maxLayers) throw new ProjectLimitError('Maipo project exceeds the layer limit.');
  const document: EditorDocument = {
    schemaVersion: 1,
    id: serialized.id,
    name: serialized.name,
    width: serialized.width,
    height: serialized.height,
    resolution: serialized.resolution,
    color: serialized.color,
    pixelAspectRatio: serialized.pixelAspectRatio,
    root,
    selection: { regions: canonicalizeRegions(serialized.selection.regions), inverted: serialized.selection.inverted },
    activeLayerIds: [...serialized.activeLayerIds],
    createdAt: serialized.createdAt,
    updatedAt: serialized.updatedAt,
    source: { kind: 'maipo', name: serialized.source?.name ?? serialized.name },
    ...(serialized.importReport ? { importReport: serialized.importReport } : {}),
    ...(serialized.guides ? { guides: serialized.guides.map((guide) => ({ ...guide })) } : {}),
    extensions: preserveUnknownFields(serialized, ['schemaVersion', 'id', 'name', 'width', 'height', 'resolution', 'color', 'pixelAspectRatio', 'root', 'selection', 'activeLayerIds', 'createdAt', 'updatedAt', 'source', 'importReport', 'guides', 'extensions', 'dirty'], serialized.extensions),
  };
  return { document, notices };
}

export class MaipoProjectCodec implements DocumentCodec {
  readonly id = 'maipo';
  readonly label = 'Maipo project';

  canRead(file: File): boolean {
    return /\.(maipo|vireo)$/i.test(file.name) || file.type === 'application/vnd.maipo.project+zip' || file.type === 'application/vnd.vireo.project+zip';
  }

  async read(file: File, context: CodecContext = {}): Promise<{ document: EditorDocument }> {
    if (file.size > MAX_EXPANDED_BYTES) throw new Error('Maipo project exceeds the 2 GiB archive safety limit.');
    return this.readBytes(new Uint8Array(await file.arrayBuffer()), file.name, context);
  }

  async readBytes(bytes: Uint8Array, sourceName: string, context: CodecContext = {}): Promise<{ document: EditorDocument }> {
    if (bytes.byteLength > MAX_EXPANDED_BYTES) throw new Error('Maipo project exceeds the 2 GiB archive safety limit.');
    context.onProgress?.({ phase: 'validating', progress: 0.1, message: 'Checking project archive' });
    const result = await deserializeProject(bytes, context.signal);
    await prepareNativeDocumentResources(result.document, context.signal);
    try {
      if (result.notices.length) {
        result.document.importReport = { codec: 'Maipo project reader', sourceName, notices: result.notices };
      }
      context.onProgress?.({ phase: 'converting', progress: 1, message: 'Project ready' });
      return { document: result.document };
    } catch (error) {
      releaseNativeDocumentResources(result.document);
      throw error;
    }
  }
}
